import { useMutation, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import moment from "moment";
import { v4 } from "uuid";

import { BsArrowRight } from "react-icons/bs";
import {
  FETCH_PLAN_AND_ODOO_INTERNAL_TRANSFER,
  SYNC_PLAN_TO_ODOO,
} from "./graphql";

const SyncPlanToTransferView = ({ planId, odooObject, didSyncedToOdoo }) => {
  const { loading, error, data } = useQuery(
    FETCH_PLAN_AND_ODOO_INTERNAL_TRANSFER,
    {
      variables: { ids: [odooObject.objectId], planId },
      fetchPolicy: "network-only",
    },
  );
  const [sync, syncRes] = useMutation(SYNC_PLAN_TO_ODOO, {
    variables: { planId, instance: "AB" },
    onCompleted() {
      Alert("success", "Sync Success");
      didSyncedToOdoo();
    },
    onError(error) {
      Alert("error", error.message);
    },
    refetchQueries: [FETCH_PLAN_AND_ODOO_INTERNAL_TRANSFER],
    awaitRefetchQueries: true,
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const odooTransfer = JSON.parse(data.odooTransfer)[0];
  const isDraftOdooTransfer = odooTransfer.state === "draft";

  console.log("odooTransfer", odooTransfer);

  const planLines = data.productionPlan.finalLines
    .reduce((res, i) => {
      const found = res.find((x) => x.product.id === i.product.id);
      if (found)
        return res.map((x) =>
          x.id === found.id
            ? {
                ...x,
                qty: x.qty + i.finalQty,
              }
            : x,
        );
      return [...res, { ...i, id: v4(), qty: i.finalQty }];
    }, [])
    .map((planLine) => {
      const odooLines = odooTransfer.move_ids_without_package.filter(
        (odooLine) =>
          odooLine.product_id &&
          odooLine.product_id.id === planLine.product.odooId,
      );
      const odooTotalQty = odooLines.reduce(
        (sum, odooLine) => sum + odooLine.product_uom_qty,
        0,
      );
      const isSynced = odooTotalQty === planLine.qty;
      return { ...planLine, odooTotalQty, isSynced };
    });

  const planProductOdooIds = planLines.map((i) => i.product.odooId);

  const odooLinesWithNotFoundInPlanLines =
    odooTransfer.move_ids_without_package.filter(
      (i) => !i.product_id || !planProductOdooIds.includes(i.product_id.id),
    );

  const isSynced = planLines.every((line) => line.isSynced);
  const hasQtyIncrease = planLines.some((line) => line.qty > line.odooTotalQty);
  const shouldDisableSync = !isDraftOdooTransfer && !hasQtyIncrease;

  return (
    <div>
      <div className="mt-4 -mx-2">
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th className="text-right text-purple-600">Odoo QTY</th>
              <th className="text-right">Plan QTY</th>
            </tr>
          </thead>
          <tbody>
            {planLines.map((line, planLineIndex) => (
              <tr
                key={planLineIndex}
                className={`
                  border-b dark:border-gray-700
                  ${line.qty !== line.odooTotalQty ? "text-red-600" : ""}`}
              >
                <td>
                  {planLineIndex + 1}. [{line.product.number}]{" "}
                  {line.product.name}
                </td>
                <td className="text-right">{line.odooTotalQty}</td>
                <td className="text-right">{line.qty}</td>
              </tr>
            ))}

            {odooLinesWithNotFoundInPlanLines.length > 0 && (
              <>
                <tr>
                  <td
                    colSpan={3}
                    className="text-sm text-red-600 font-semibold"
                  >
                    ⚠️ These lines exist in Odoo but are not in the plan. WIS
                    will keep them. Please review and update them manually in
                    Odoo if needed.
                  </td>
                </tr>

                {odooLinesWithNotFoundInPlanLines.map((line, odooLineindex) => (
                  <tr key={odooLineindex} className={`border-y text-red-600`}>
                    <td>
                      {odooLineindex + 1}. {line.product_id.display_name}
                    </td>
                    <td className="text-right">{line.product_uom_qty}</td>
                    <td className="text-right">0</td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center space-x-3 mt-6">
        <label>Ready Date: </label>
        <span>{moment(odooTransfer.scheduled_date).format("YYYY-MM-DD")}</span>
        <BsArrowRight />
        <span>
          {moment(data.productionPlan.finalReadyDate).format("YYYY-MM-DD")}
        </span>
      </div>

      {isSynced ? (
        <div className="mt-8 text-green-600 font-semibold">
          ✅ This plan is synced with Odoo - {odooObject.objectName}
        </div>
      ) : (
        <>
          <div className="mt-8 text-sm text-orange-600 font-semibold leading-relaxed">
            <p>Sync behavior ⚠️</p>
            {isDraftOdooTransfer ? (
              <p>
                This Odoo transfer is in draft. WIS will remove all current
                lines and recreate them using the plan quantities.
              </p>
            ) : (
              <>
                <p>
                  This Odoo transfer is not in draft. To avoid business issues,
                  WIS does not change quantity on existing Odoo lines directly.
                </p>
                <p>1. Quantity increase: WIS adds a new line.</p>
                <p>
                  2. Quantity decrease: WIS skips that line. Please adjust the
                  quantity manually in Odoo.
                </p>
              </>
            )}
          </div>

          <div className="mt-8 text-sm">
            <Button
              primary
              loading={syncRes.loading}
              disabled={shouldDisableSync}
              onClick={sync}
            >
              Sync to {odooObject.objectName}
            </Button>
            {shouldDisableSync && (
              <p className="mt-2 text-orange-600 font-semibold">
                Nothing to sync: no quantity increases were found. For
                non-draft transfers, decreases must be handled manually in
                Odoo.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SyncPlanToTransferView;
